import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { baseUrl } from '../utils';
import axios from 'axios';

const initialState = {
  loading: false,
  data: null,
  users: [],
};

export const getUserProfile = createAsyncThunk('userProfile/getUserProfile', async () => {
  try {
    let token = localStorage.getItem('x-auth-token');
    const { data } = await axios({
      method: 'GET',
      url: `${baseUrl}/provider/profile`,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return data;
  } catch (error) {
    console.log(error);
  }
});

export const listAllUsers = createAsyncThunk('userProfile/listAllUsers', async () => {
  try {
    let token = localStorage.getItem('x-auth-token');
    const { data } = await axios({
      method: 'GET',
      url: `${baseUrl}/provider/profile/users`,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return data;
  } catch (error) {
    console.log(error);
  }
});

export const changeUserStatus = createAsyncThunk(
  'userProfile/changeUserStatus',
  async ({ userId, openNotificationWithIcon }) => {
    try {
      let token = localStorage.getItem('x-auth-token');
      const { data } = await axios({
        method: 'POST',
        url: `${baseUrl}/user/${userId}/activate`,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      openNotificationWithIcon('success', data.message);
      return userId;
    } catch (error) {
      openNotificationWithIcon('error');
    }
  },
);

export const deleteUserProfile = createAsyncThunk(
  'userProfile/deleteUserProfile',
  async ({ id, openNotificationWithIcon }) => {
    let token = localStorage.getItem('x-auth-token');
    try {
      const response = await axios.delete(`${baseUrl}/user/${id}`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      console.log(response);
      openNotificationWithIcon('success', response.data.message);
      return id;
    } catch (error) {
      openNotificationWithIcon('error');
      console.log(error);
    }
  },
);

export const addNewUser = createAsyncThunk('userProfile/addNewUser', async (data) => {
  try {
    let token = localStorage.getItem('x-auth-token');
    const response = await axios({
      method: 'POST',
      url: `${baseUrl}/user`,
      data: data.data,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.data.api.responseCode === 2010) {
      data.notification('success', response.data.message);
      data.addUserScreen(false);
    }
    if (response.data.api.responseCode === 3530) {
      data.notification('error', response.data.message, 'Email already in use');
    }
    console.log(response);
    return response.data;
  } catch (error) {
    const errorData = error?.response?.data;
    const resultData = errorData?.result;
    
    const formatFieldName = (field) => {
      return field
        .replace(/([A-Z])/g, ' $1') 
        .replace(/_/g, ' ') 
        .replace(/^\w/, (c) => c.toUpperCase()) 
        .trim();
    };
    
    if (resultData?.field_errors) {
      const fieldErrors = resultData.field_errors;
      const errorMessages = Object.entries(fieldErrors)
        .map(([field, messages]) => `• ${formatFieldName(field)}: ${messages.join(', ')}`)
        .join('\n');
      data.notification('error', errorMessages, 'Please fix the following errors:');
    } else {
      // Fallback to general error message
      const errorMessage = resultData?.message || errorData?.message || error?.message || 'Something went wrong';
      data.notification('error', errorMessage, resultData?.error || 'Error');
    }
  }
});

export const changePassword = createAsyncThunk('userProfile/changePassword', async (data) => {
  try {
    let token = localStorage.getItem('x-auth-token');
    const response = await axios({
      method: 'PATCH',
      url: `${baseUrl}/user/account/password`,
      data: data.data,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    data.notification('success');
    data.navigate(false);
    console.log(data);
    return response.data;
  } catch (error) {
    data.notification('error', error.response.data.message);
  }
});

export const changeLogo = createAsyncThunk(
  'userProfile/changeLogo',
  async ({ formData, openNotificationWithIcon }, { rejectWithValue }) => {
    let token = localStorage.getItem('x-auth-token');
    try {
      const response = await axios({
        method: 'POST',
        url: `${baseUrl}/provider/profile/logo`,
        data: formData,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.api.responseCode === 2020) {
        openNotificationWithIcon('success', response.data.message);
        // Return the logo data to update the state
        return response.data.result.logo;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      openNotificationWithIcon('error', error.response?.data?.message || 'Failed to upload logo');
      return rejectWithValue(error.response?.data);
    }
  },
);

export const removeLogo = createAsyncThunk(
  'userProfile/removeLogo',
  async ({ openNotificationWithIcon }, { rejectWithValue }) => {
    let token = localStorage.getItem('x-auth-token');
    try {
      const response = await axios({
        method: 'DELETE',
        url: `${baseUrl}/provider/profile/logo`,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.api.responseCode === 2020) {
        openNotificationWithIcon('success', response.data.message);
        return true;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      openNotificationWithIcon('error', error.response?.data?.message || 'Failed to remove logo');
      return rejectWithValue(error.response?.data);
    }
  },
);

export const UserProfileSlice = createSlice({
  name: 'userProfile',
  initialState: initialState,
  reducers: {
    logOut: (state) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.token = null;
      localStorage.clear();
    },
  },
  extraReducers(builder) {
    builder.addCase(changePassword.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(changePassword.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload?.result;
    });
    builder.addCase(changePassword.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getUserProfile.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getUserProfile.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload?.result;
    });
    builder.addCase(getUserProfile.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(listAllUsers.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(listAllUsers.fulfilled, (state, action) => {
      state.loading = false;
      state.users = action.payload?.result;
    });
    builder.addCase(listAllUsers.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(changeUserStatus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(changeUserStatus.fulfilled, (state, action) => {
      state.loading = false;
      state.users = state.users.map((item) => {
        if (item.user_id === action.payload) {
          return { ...item, status: 'Active' };
        }
        return item;
      });
    });

    builder.addCase(addNewUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addNewUser.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload?.result) {
        state.users = [...state.users, action.payload?.result];
      }
    });
    builder.addCase(changeLogo.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(changeLogo.fulfilled, (state, action) => {
      state.loading = false;

      // Initialize files array if it doesn't exist
      if (!state.data.files) {
        state.data.files = [];
      }

      // Find and update or add the logo in the files array
      const logoIndex = state.data.files.findIndex((file) => file.document_type === 'logo');

      if (logoIndex !== -1) {
        // Update existing logo
        state.data.files[logoIndex] = action.payload;
      } else {
        // Add new logo to files array
        state.data.files.push(action.payload);
      }
    });
    builder.addCase(changeLogo.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(removeLogo.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(removeLogo.fulfilled, (state) => {
      state.loading = false;

      // Remove logo from files array
      if (state.data.files) {
        state.data.files = state.data.files.filter((file) => file.document_type !== 'logo');
      }
    });
    builder.addCase(removeLogo.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteUserProfile.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteUserProfile.fulfilled, (state, action) => {
      state.loading = false;
      state.users = state.users.filter((item) => item.user_id !== action.payload);
    });
    builder.addCase(deleteUserProfile.rejected, (state) => {
      state.loading = false;
    });
  },
});

export default UserProfileSlice.reducer;
